import React, { useState } from 'react'
import { Button, Grid, Image, Segment } from 'semantic-ui-react'
import * as R from 'ramda'

import useAxios, { mutate } from 'hooks/useAxios'
import { toast } from 'libs/toast'
import ActivateIntegrationButtonSection from 'components/ActivateIntegrationButtonSection'
import { IntegrationTemplate } from '.'
import SurveyToggleSettings from 'components/SurveyToggleSettings'
import SurveySentimentToggleSettings from 'components/SurveySentimentToggleSettings'
import CyclrCustomFieldForm from 'components/CyclrCustomFieldForm'
import { Loader } from 'shared/components/Loader'
import CyclrWebhookSetting from 'components/CyclrWebhookSetting'
import useModal from 'hooks/useModal'
import { parseErrorMessage } from 'libs/parseErrorMessage'
import { combineVariableWithCustomField } from './utils'

type AccordionContentProps = {
  template: IntegrationTemplate
  connectorId: string
  connectorName: string
  isAuthenticated: boolean
  activateIntegration: (template: IntegrationTemplate) => void
  deactivateIntegration: (cycle_id: string | null) => void
  deleteIntegration: (
    cycle_id: string | null,
    close: Function | null,
    setLoadingModal: Function | null
  ) => void
  isActivating: boolean
  isExpanded: boolean
}

export default function AccordionContent({
  template,
  connectorId,
  connectorName,
  isAuthenticated,
  activateIntegration,
  deactivateIntegration,
  deleteIntegration,
  isActivating,
  isExpanded,
}: AccordionContentProps) {
  const { Modal, open, close, isOpen } = useModal()
  const [loadingModal, setLoadingModal] = useState(false)

  function handelDelete() {
    setLoadingModal(true)
    deleteIntegration(template.cycle_id, close, setLoadingModal)
  }

  const hasImage = template.image && template.image !== 'null'

  return (
    <>
      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={10}>
            <div dangerouslySetInnerHTML={{ __html: template.description }} />
            <div className="mt-4">
              <ActivateIntegrationButtonSection
                activateIntegration={() => activateIntegration(template)}
                deactivateIntegration={() => deactivateIntegration(template.cycle_id)}
                deleteIntegration={open}
                hideDeleteButton={template.cycle_id == null}
                loading={isActivating}
                isAuthenticated={isAuthenticated}
                isIntegrationActive={template.is_active}
                integrationType={connectorName}
                position="top left"
              />
            </div>
          </Grid.Column>
          {hasImage && (
            <Grid.Column width={6} textAlign="right">
              <Segment className="!mb-4">
                <Image src={template.image} />
              </Segment>
            </Grid.Column>
          )}
        </Grid.Row>
      </Grid>
      <TemplateSettings
        connectorId={connectorId}
        template={template}
        connectorName={connectorName}
        isAuthenticated={isAuthenticated}
        isExpanded={isExpanded}
      />
      {isOpen && (
        <Modal size="tiny">
          <DeleteCycleModal
            modal={Modal}
            close={close}
            loading={loadingModal}
            handelDelete={handelDelete}
          />
        </Modal>
      )}
    </>
  )
}

type TemplateSettingsProps = {
  template: IntegrationTemplate
  connectorId: string
  connectorName: string
  isAuthenticated: boolean
  isExpanded: boolean
}
function TemplateSettings({
  template,
  connectorName,
  isAuthenticated,
  connectorId,
  isExpanded,
}: TemplateSettingsProps) {
  const params = template.cycle_id ? `?cycle_id=${template.cycle_id}` : ''
  // Skip the fetch until the panel is expanded — avoids the on-mount N+1 fanout
  // to the external Cyclr API. useAxios caches the GET by URL, so it fires once.
  const { data: templateSetting, loading: isFetching } = useAxios({
    url: `/cyclr/templates/${template.template_id}/settings/${params}`,
    skip: !isExpanded,
  })

  const { fetch: callUpdateIsEverySurveyIncludedStatus } = useAxios({
    method: 'PUT',
    skip: true,
  })

  const { fetch: callUpdateSurveySettings } = useAxios({
    method: 'PUT',
    skip: true,
  })

  const { fetch: callUpdateCustomFieldSettings, loading: updatingCustomFields } = useAxios({
    method: 'PUT',
    skip: true,
  })

  const { fetch: callUpdateVariable, loading: updatingVariable } = useAxios({
    method: 'PUT',
    skip: true,
  })

  const {
    fetch: callLookupField,
    loading: lookupLoading,
    data: lookupResponseData,
  } = useAxios({
    url: 'cyclr/step/lookup/',
    method: 'POST',
    skip: true,
  })

  // `templateSetting === undefined` means the lazy GET hasn't resolved yet (SWR
  // returns undefined until the first fetch settles, then the response body).
  // Treat that as loading so an expanded panel shows the Loader instead of
  // briefly flashing 'No settings' in the gap before the fetch dispatches. A
  // genuinely empty fetched result is defined (null / empty object), so it still
  // falls through to the 'No settings' branch below.
  if (isFetching || (isExpanded && templateSetting === undefined)) {
    return <Loader withContainer />
  }

  if (!templateSetting) {
    return 'No settings'
  }

  function updateIsEverySurveyIncludedStatus(cycleId, value) {
    callUpdateIsEverySurveyIncludedStatus({
      url: `/cyclr/cycles/${cycleId}/settings/`,
      data: { is_every_survey_included: value },
    })
      .then(() => {
        toast.success('Settings updated.')
      })
      .catch((error) => {
        toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
      })
  }

  function updateSurveySettings(cycleId, templateOptionType, { surveyToken, name, value }) {
    callUpdateSurveySettings({
      url: `/cyclr/cycles/${cycleId}/surveys/${surveyToken}/settings/`,
      data: {
        option_name: name,
        option_value: value,
        option_type: templateOptionType,
      },
    })
      .then(() => {
        toast.success('Settings updated.')
      })
      .catch((error) => {
        toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
      })
  }

  function updateCustomFieldSettings(cycleId, { customFields, destinationStepId }) {
    if (R.isEmpty(customFields)) {
      toast.success('Settings updated.')
      return
    }
    const fields: object[] = []
    const parameters: object[] = []
    const variables: object[] = []

    customFields.forEach((customField) => {
      if (customField.IsVariable) {
        const { DisplayName, SystemName, Value, MappingType } = customField
        variables.push({
          DisplayName,
          SystemName,
          Value,
          MappingType,
        })
      } else {
        const field = {
          destination_step_id: destinationStepId,
          destination_field_id: customField.Field.Id,
          source_field_id: customField.SourceFieldId,
          source_step_id: customField.SourceStepId,
          mapping_type: customField.MappingType,
          value: customField.Value,
          trigger_value: customField.TriggerValue,
          trigger_value_display_name: customField.TriggerValueDisplayName,
        }

        if (customField.IsParameter) {
          parameters.push(field)
        } else {
          fields.push(field)
        }
      }
    })

    const blankPromise = Promise.resolve()

    const variablePromise =
      variables.length > 0
        ? callUpdateVariable({
            url: `/cyclr/cycles/${cycleId}/settings/variables/`,
            data: { variables },
          })
        : blankPromise

    const hasCustomFieldChanged = parameters.length > 0 || fields.length > 0
    const customFieldPromise = hasCustomFieldChanged
      ? callUpdateCustomFieldSettings({
          url: `/cyclr/cycles/${cycleId}/settings/custom-fields/`,
          data: { parameters, custom_fields: fields },
        })
      : blankPromise

    Promise.all([variablePromise, customFieldPromise])
      .then(() => {
        toast.success('Settings updated.')
        mutate(
          `/cyclr/account/connectors/${connectorId}/`,
          (state) => {
            if (hasCustomFieldChanged) {
              const index = R.findIndex(R.propEq('template_id', template.template_id))(
                state.templates
              )
              const updatedCycleIdTemplates = R.assocPath(
                [index, 'cycle_id'],
                cycleId,
                state.templates
              )
              const newTemplates = R.assocPath([index, 'is_active'], true, updatedCycleIdTemplates)
              return { ...state, templates: newTemplates }
            } else {
              // Updating the variable api does not automatically stop the integration
              // and revert to auto-activate, like it does with custom field settings.
              return state
            }
          },
          false
        )
      })
      .catch((error) => {
        toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
      })
  }

  function getSentimentToggleSurveySettings(templateSetting) {
    const sentimentToggleSettings = R.find(R.propEq('type', 'sentiment_toggle'))(
      templateSetting.settings
    )
    return R.prop('survey_settings', sentimentToggleSettings) || []
  }

  function getSurveyToggleSurveySettings(templateSetting) {
    const surveyToggleSettings = R.find(R.propEq('type', 'survey_toggle'))(templateSetting.settings)
    return R.prop('survey_settings', surveyToggleSettings) || []
  }

  function getConnectorPropertySettings(templateSetting) {
    const connectorPropertySettings = R.prop('connector_properties', templateSetting)
    return connectorPropertySettings || []
  }

  function handleCallLookupField(data, hasNextTrigger) {
    if (hasNextTrigger) {
      callLookupField({ data })
    }
  }

  const everyResponseOptionLabel =
    template.integration_level === 'ticket_integration'
      ? `Every response from ${connectorName} surveys`
      : 'Every response from all current and future surveys'

  const templateOptions = combineVariableWithCustomField(templateSetting.settings)
  return templateOptions.map((setting) => {
    const key = `${template.template_id}-${setting.type}`
    if (setting.type === 'survey_toggle') {
      return (
        <SurveyToggleSettings
          key={key}
          isAuthenticated={isAuthenticated}
          isIntegrationActive={template.is_active}
          isEverySurveyIncluded={templateSetting.is_every_survey_included}
          surveySettings={getSurveyToggleSurveySettings(templateSetting)}
          onChange={(newSettings) =>
            updateSurveySettings(template.cycle_id, setting.type, newSettings)
          }
          onIsEverySurveyIncludedChange={(value) =>
            updateIsEverySurveyIncludedStatus(template.cycle_id, value)
          }
          everyResponseOptionLabel={everyResponseOptionLabel}
        />
      )
    } else if (setting.type === 'sentiment_toggle') {
      return (
        <SurveySentimentToggleSettings
          key={key}
          isAuthenticated={isAuthenticated}
          isIntegrationActive={template.is_active}
          isEverySurveyIncluded={templateSetting.is_every_survey_included}
          surveySettings={getSentimentToggleSurveySettings(templateSetting)}
          onChange={(newSettings) =>
            updateSurveySettings(template.cycle_id, setting.type, newSettings)
          }
          onIsEverySurveyIncludedChange={(value) =>
            updateIsEverySurveyIncludedStatus(template.cycle_id, value)
          }
          everyResponseOptionLabel={everyResponseOptionLabel}
        />
      )
    } else if (setting.type === 'custom_field' || setting.type === 'variable') {
      return (
        <CyclrCustomFieldForm
          key={key}
          settingType={setting.type}
          connectorProperties={getConnectorPropertySettings(templateSetting)}
          additionalSettings={R.path(['additional_settings'], setting)}
          onChange={(customFieldSettings) =>
            updateCustomFieldSettings(template.cycle_id, customFieldSettings)
          }
          isSaving={updatingCustomFields || updatingVariable}
          isDisabled={!isAuthenticated || !template.cycle_id}
          isTemplateActive={template.is_active}
          handleCallLookupField={handleCallLookupField}
          lookupResponseData={lookupResponseData}
          lookupLoading={lookupLoading}
        />
      )
    } else if (setting.type === 'webhook_url') {
      const webhookUrl = setting.additional_settings.webhook_url || null

      return (
        <CyclrWebhookSetting
          key={key}
          webhookUrl={webhookUrl}
          isDisabled={!isAuthenticated || !template.is_active}
        />
      )
    } else {
      return null
    }
  })
}

const DeleteCycleModal = ({ modal, close, loading, handelDelete }) => {
  return (
    <React.Fragment>
      <modal.Header>Reset integration</modal.Header>
      <modal.Content>
        <p>
          Resetting this integration will revert all settings to their default values. This action
          is irreversible. Are you sure you want to proceed?
        </p>
      </modal.Content>
      <modal.Actions>
        <Button onClick={close} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handelDelete} loading={loading} disabled={loading} negative>
          Reset integration
        </Button>
      </modal.Actions>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/EmbeddingCyclrIntegration/AccordionContent.tsx